import { memo, MutableRefObject } from 'react'
import { alpha, useTheme } from '@mui/material'
import { background } from '../../pages/home/plChart'
import { Group } from '@visx/group'
import { BarGroup } from '@visx/shape'
import type { ScaleBand, ScaleLinear, ScaleOrdinal } from 'd3-scale'
import { AxisBottom, AxisLeft, AxisRight } from '@visx/axis'
import { GridRows } from '@visx/grid'
import { participantBarDefaultColor, ParticipantChartMetaInfo } from './OIChart'

export type OiChartRow = { date: string } & Record<string, number | string>

export type OIChartBarsLayerProps = {
    width: number
    height: number
    margin: { top: number; right: number; bottom: number; left: number }
    leftScaleWidth: number
    scaleSide?: 'left' | 'right'
    xMax: number
    yMax: number
    keys: string[]
    animatedData: OiChartRow[]
    data: OiChartRow[]
    showOI: boolean
    metaInfo: ParticipantChartMetaInfo
    dateScale: ScaleBand<string>
    x1Scale: ScaleBand<string>
    yScale: ScaleLinear<number, number>
    yLeftScale: ScaleLinear<number, number>
    colorScale: ScaleOrdinal<string, string>
    xTickValues: string[]
    yScaleMax: number
    yScaleMin: number
    /** Fewer ticks for short sidebar previews; omit for full-height charts. */
    yNumTicks?: number
    barWidthRef: MutableRefObject<number>
    yTickFormatter: (value: number | { valueOf(): number }) => string
    xTickFormatter: (value: string) => string
}

const OIChartBarsLayer = ({
    width,
    height,
    margin,
    leftScaleWidth,
    scaleSide = 'left',
    xMax,
    yMax,
    keys,
    animatedData,
    data,
    showOI,
    metaInfo,
    dateScale,
    x1Scale,
    yScale,
    yLeftScale,
    colorScale,
    xTickValues,
    yScaleMax,
    yScaleMin,
    yNumTicks,
    barWidthRef,
    yTickFormatter,
    xTickFormatter,
}: OIChartBarsLayerProps) => {
    const theme = useTheme()

    const valueRelativeToYRange = (y: number): number => {
        return (y / (yScaleMax - yScaleMin)) * yMax
    }

    return (
        <>
            <defs>
                {keys.map((key) => {
                    const color = metaInfo.colors.get(key) ?? participantBarDefaultColor
                    const rotation = key.includes('Call') ? -45 : 45
                    return (
                        <pattern
                            key={`pattern-${key}`}
                            id={`stripe-pattern-${key.replace(/\s+/g, '-')}`}
                            patternUnits="userSpaceOnUse"
                            width="8"
                            height="8"
                            patternTransform={`rotate(${rotation})`}
                        >
                            <rect width="4" height="8" fill={color} opacity="0.3" />
                            <rect x="4" width="4" height="8" fill={color} opacity="0.6" />
                        </pattern>
                    )
                })}
            </defs>
            <rect x={0} y={0} width={width} height={height} fill={background} rx={14} />
            <Group top={margin.top} left={margin.left + leftScaleWidth} pointerEvents="none">
                <GridRows
                    scale={yScale}
                    width={xMax}
                    height={yMax}
                    numTicks={yNumTicks}
                    stroke={
                        theme.palette.mode === 'dark'
                            ? theme.palette.divider
                            : alpha(theme.palette.divider, 0.5)
                    }
                />
                <BarGroup
                    data={animatedData}
                    keys={keys}
                    height={yMax}
                    x0={(d) => String(d.date)}
                    x0Scale={dateScale}
                    x1Scale={x1Scale}
                    yScale={yScale}
                    color={colorScale}
                >
                    {(barGroups) =>
                        barGroups?.map((barGroup, barGroupIndex) => {
                            if (barGroupIndex === 0) {
                                let w = 0
                                barGroup.bars.forEach((bar) => {
                                    w += bar.width
                                })
                                barWidthRef.current = w
                            }
                            return (
                                <Group key={`bar-group-${barGroup.index}-${barGroup.x0}`} left={barGroup.x0}>
                                    {barGroup.bars?.map((bar) => {
                                        const item = animatedData[barGroupIndex]
                                        const fromKey = `${bar.key} From`
                                        const toKey = `${bar.key} To`
                                        const fromValue = Number(item?.[fromKey] || 0)
                                        const toValue = Number(item?.[toKey] || 0)
                                        const change = toValue - fromValue
                                        const currentValue = bar.value
                                        const barY =
                                            currentValue < 0
                                                ? valueRelativeToYRange(yScaleMax)
                                                : yMax -
                                                  valueRelativeToYRange(currentValue) -
                                                  Math.abs(valueRelativeToYRange(yScaleMin))
                                        const barHeight = valueRelativeToYRange(Math.abs(currentValue))

                                        if (!showOI) {
                                            return (
                                                <rect
                                                    key={`bar-group-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                                                    x={bar.x}
                                                    y={barY}
                                                    width={bar.width}
                                                    height={barHeight}
                                                    fill={bar.color}
                                                    rx={0}
                                                />
                                            )
                                        }

                                        if (Math.abs(change) < 0.01) {
                                            return (
                                                <rect
                                                    key={`bar-group-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                                                    x={bar.x}
                                                    y={barY}
                                                    width={bar.width}
                                                    height={barHeight}
                                                    fill={bar.color}
                                                    rx={0}
                                                />
                                            )
                                        }
                                        if (change < 0) {
                                            const fromBarY =
                                                fromValue < 0
                                                    ? valueRelativeToYRange(yScaleMax)
                                                    : yMax -
                                                      valueRelativeToYRange(fromValue) -
                                                      Math.abs(valueRelativeToYRange(yScaleMin))
                                            const fromBarHeight = valueRelativeToYRange(Math.abs(fromValue))

                                            return (
                                                <Group
                                                    key={`bar-group-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                                                >
                                                    <rect
                                                        x={bar.x}
                                                        y={fromBarY}
                                                        width={bar.width}
                                                        height={fromBarHeight}
                                                        fill="none"
                                                        stroke={bar.color}
                                                        strokeWidth={1}
                                                        rx={0}
                                                    />
                                                    <rect
                                                        x={bar.x}
                                                        y={barY}
                                                        width={bar.width}
                                                        height={barHeight}
                                                        fill={bar.color}
                                                        rx={0}
                                                    />
                                                </Group>
                                            )
                                        }

                                        const fromBarY =
                                            fromValue < 0
                                                ? valueRelativeToYRange(yScaleMax)
                                                : yMax -
                                                  valueRelativeToYRange(fromValue) -
                                                  Math.abs(valueRelativeToYRange(yScaleMin))
                                        const fromBarHeight = valueRelativeToYRange(Math.abs(fromValue))
                                        const patternId = `stripe-pattern-${bar.key.replace(/\s+/g, '-')}`

                                        return (
                                            <Group
                                                key={`bar-group-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                                            >
                                                <rect
                                                    x={bar.x}
                                                    y={barY}
                                                    width={bar.width}
                                                    height={barHeight}
                                                    fill={`url(#${patternId})`}
                                                    rx={0}
                                                />
                                                <rect
                                                    x={bar.x}
                                                    y={fromBarY}
                                                    width={bar.width}
                                                    height={fromBarHeight}
                                                    fill={bar.color}
                                                    rx={0}
                                                />
                                            </Group>
                                        )
                                    })}
                                </Group>
                            )
                        })
                    }
                </BarGroup>
            </Group>
            <AxisBottom
                top={yMax + margin.top}
                left={leftScaleWidth}
                scale={dateScale}
                hideAxisLine
                tickLength={scaleSide === 'right' ? 0 : 8}
                tickLabelProps={{
                    fill: theme.palette.text.secondary,
                    fontSize: scaleSide === 'right' ? 10 : 12,
                    textAnchor: 'middle',
                    dy: scaleSide === 'right' ? 4 : undefined,
                }}
                tickValues={xTickValues}
                numTicks={animatedData.length}
                tickFormat={xTickFormatter}
            />
            {data.length > 0 && scaleSide === 'right' && (
                <AxisRight
                    scale={yLeftScale}
                    top={margin.top}
                    left={margin.left + xMax}
                    hideAxisLine
                    tickLength={0}
                    numTicks={yNumTicks}
                    tickLabelProps={{
                        fill: theme.palette.text.secondary,
                        fontSize: 11,
                        textAnchor: 'start',
                        dx: 2,
                    }}
                    labelProps={{
                        fill: theme.palette.text.secondary,
                        fontSize: 10,
                        textAnchor: 'middle',
                    }}
                    tickFormat={yTickFormatter}
                />
            )}
            {data.length > 0 && scaleSide !== 'right' && (
                <AxisLeft
                    scale={yLeftScale}
                    top={margin.top}
                    left={margin.left + 50}
                    hideAxisLine
                    numTicks={yNumTicks}
                    tickLabelProps={{
                        fill: theme.palette.text.secondary,
                        fontSize: 11,
                        textAnchor: 'end',
                    }}
                    labelProps={{
                        fill: theme.palette.text.secondary,
                        fontSize: 10,
                        textAnchor: 'middle',
                        dx: 5,
                    }}
                    tickFormat={yTickFormatter}
                />
            )}
        </>
    )
}

export default memo(OIChartBarsLayer)
